import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, ellipsisAfterNumberOfLines} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CardAvatar} from '../../../../../components/common/avatars/cardAvatar';
import {useAppDispatch} from '../../../../../helpers/reduxHelpers';
import {filterMessageReceivers} from '../../../../../models/api/analyticsTimelineEventModel';
import {CardModel} from '../../../../../models/api/cardModel';
import {buildContactFromContactUid} from '../../../../../models/api/contactModel';
import {MessageModel} from '../../../../../models/api/messageModel';
import {popoutConversationViewerAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {StyledFlex} from '../../../reportViewer/reportCommons';

/*
 * Styles.
 */

const StyledMessageCardWrapper = styled.div`
  max-height: 200px;
  border: 1px solid ${(p) => p.theme.greys.shade40};
  border-radius: 16px;
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledMessageHeader = styled.div`
  padding: 14px 16px 13px 16px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade40};
`;

const StyledParticipants = styled.div`
  flex: 1;
  margin-left: 16px;
  overflow: hidden;
`;

const StyledParticipant = styled.div`
  ${ellipsis()}
  &:first-child {
    margin-bottom: 2px;
  }
`;

const StyledSender = styled.span`
  color: ${(p) => p.theme.interactiveText};
  font-weight: ${fontWeights.semibold};
`;

const StyledToLabel = styled.span`
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledReceiver = styled.span`
  color: ${(p) => p.theme.interactiveText};
`;

const StyledMessageBodyWrapper = styled.div`
  /* ellipsisAfterNumberOfLines doesn't seem to work well with padding so we add an outer wrapper here. */
  padding: 13px 16px;
`;

const StyledMessageBody = styled.div`
  max-height: 134px;
  line-height: 20px;
  ${ellipsisAfterNumberOfLines(5)}
`;

const StyledPopoutButtonWrapper = styled.div`
  align-self: start;
  margin-left: 15px;
  margin-top: -5px;
`;

/*
 * Props.
 */

interface TimelineMessagePreviewProps {
  activityId: number;
  message: MessageModel;
  from: CardModel | undefined; // In some cases the message sender doesn't have a card.
  fallbackSenderHandle: string; // In this case we fallback to the FROM recipient's handle.
}

/*
 * Component.
 */

export const TimelineMessagePreview: FC<TimelineMessagePreviewProps> = ({
  activityId,
  message,
  from,
  fallbackSenderHandle,
}) => {
  const dispatch = useAppDispatch();

  const receivers = filterMessageReceivers(message.recipients);

  // We need to extract the handle from the contact uid.
  const senderHandle = from ? retrieveCardHandle(from) : fallbackSenderHandle;

  const onRequestPopout = () => {
    dispatch(popoutConversationViewerAction(message.conversationId, activityId));
  };

  return (
    <StyledMessageCardWrapper>
      <StyledMessageHeader>
        <StyledFlex>
          {from && <CardAvatar size={VisualSizesEnum.LARGE} card={from} />}
          <StyledParticipants>
            <StyledParticipant>
              <StyledSender>{from?.name ?? senderHandle}</StyledSender>
              {senderHandle && ` <${senderHandle}>`}
            </StyledParticipant>
            <StyledParticipant>
              <StyledToLabel>
                <Trans id="email-recipient-to">To:</Trans>{' '}
              </StyledToLabel>
              {receivers.map((recipient, index) => (
                <span key={recipient.handle}>
                  <StyledReceiver>{recipient.name ?? recipient.handle}</StyledReceiver>{' '}
                  {`<${recipient.handle}>`}
                  {index < receivers.length - 1 && ', '}
                </span>
              ))}
            </StyledParticipant>
          </StyledParticipants>
          {renderPopoutButton(onRequestPopout)}
        </StyledFlex>
      </StyledMessageHeader>
      <StyledMessageBodyWrapper>
        <StyledMessageBody>{message.blurb}...</StyledMessageBody>
      </StyledMessageBodyWrapper>
    </StyledMessageCardWrapper>
  );
};

/*
 * Helpers.
 */

function renderPopoutButton(onRequestPopout: Handler) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledPopoutButtonWrapper>
          <IconButton
            size={VisualSizesEnum.SMALL}
            iconName="popoutSmall"
            onClick={onRequestPopout}
            color={theme.greys.shade50}
            label={<Trans id="analytics-conversation-details-panel-popout">View in external window</Trans>}
          />
        </StyledPopoutButtonWrapper>
      )}
    </UserThemeConsumer>
  );
}

// This extracts the handle from the first contact uid, normally the first entry should be an email handle.
function retrieveCardHandle(card: CardModel) {
  const firstContactUid = card.contactUids[0];
  if (!firstContactUid) {
    return '';
  }

  const {handle} = buildContactFromContactUid(firstContactUid);
  return handle;
}
